<template>
  <div :class="['filter-container', { 'filter-container--with-navigation': hasNavigation }]">
    <b-button
      v-if="hasNavigation"
      class="scroll-btn"
      :disabled="isAtStart"
      aria-label="Scroll filters left"
      @click="handleScroll('left')"
    >
      <i class="mc-fa icon-outline_chevron-left" />
    </b-button>
    <div ref="filterContent" class="filter-content">
      <b-button
        v-for="filterItem in filterItems"
        :key="filterItem.id"
        variant="light"
        :class="{ active: isActive(filterItem.id), 'fw-normal': isNormal }"
        @click="toggleActive(filterItem.id)"
      >
        {{ $t(filterItem.label) }}
      </b-button>
    </div>
    <b-button
      v-if="hasNavigation"
      class="scroll-btn"
      :disabled="isAtEnd"
      aria-label="Scroll filters right"
      @click="handleScroll('right')"
    >
      <i class="mc-fa icon-outline_chevron-right" />
    </b-button>
  </div>
</template>

<script>
export default {
  props: {
    // This is a v-model prop that is used to control the active filters
    // it would be an array of id from the filters
    // e.g.
    // 3 Active   = [3, 4, 7]
    // All active = []
    activeKeys: {
      type: Array,
      default: () => [],
    },
    // An array of object containing the available filter data
    // filters that indicate all filter should have id of 'all'
    // e.g.
    // [{id: 'all', label: 'Semua'}, {id: 1, label: 'Aktif'}, ...]
    filterItems: {
      type: Array,
      default: () => [],
    },
    isMultiple: {
      type: Boolean,
      default: false,
    },
    isNormal: {
      type: Boolean,
      default: false,
    },
    hasNavigation: {
      type: Boolean,
      default: false,
    },
    hasSelectAll: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:activeKeys'],
  data() {
    return {
      scrollAmount: 200, //pixels
      isAtStart: true,
      isAtEnd: false,
    }
  },
  computed: {
    computedActiveKeys: {
      get() {
        return this.activeKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {})
      },
      set(value) {
        const activeKeys = [...Object.keys(value).filter((key) => value[key])]
        const totalItems = this.hasSelectAll ? this.filterItems.length - 1 : this.filterItems.length

        if (value.all || activeKeys.length >= totalItems) {
          return this.$emit('update:activeKeys', [])
        }

        this.$emit('update:activeKeys', activeKeys)
      },
    },
  },
  mounted() {
    this.updateButtonStates()
    this.$refs.filterContent.addEventListener('scroll', this.updateButtonStates)
  },
  beforeDestroy() {
    this.$refs.filterContent.removeEventListener('scroll', this.updateButtonStates)
  },
  methods: {
    isActive(id) {
      const isAllActive =
        this.activeKeys.length === 0 || this.activeKeys.length >= this.filterItems.length - 1

      if (id === 'all' && isAllActive) return true
      return this.computedActiveKeys[id]
    },
    toggleActive(id) {
      if (id === 'all') {
        this.computedActiveKeys = { all: true }
        return
      }

      let activeKeys = {
        all: false,
        [id]: !this.computedActiveKeys[id],
      }

      if (this.isMultiple) {
        activeKeys = {
          ...Object.fromEntries(
            Object.entries(this.computedActiveKeys).filter(([value]) => value !== null)
          ),
          ...activeKeys,
        }
        const allFalse = Object.values(activeKeys).every((value) => value === false)
        if (allFalse) activeKeys = { all: true }
      }

      this.computedActiveKeys = activeKeys
    },
    handleScroll(params) {
      let attr = {}
      const filterContent = this.$refs.filterContent
      if (params == 'left') {
        attr = {
          top: 0,
          left: -this.scrollAmount,
          behavior: 'smooth',
        }
      } else {
        attr = {
          top: 0,
          left: this.scrollAmount,
          behavior: 'smooth',
        }
      }
      filterContent.scrollBy(attr)
      this.$nextTick(() => {
        this.updateButtonStates()
      })
    },
    updateButtonStates() {
      const filterContent = this.$refs.filterContent
      if (!filterContent) return

      const maxScrollLeft = Math.max(filterContent.scrollWidth - filterContent.clientWidth, 0)
      this.isAtStart = filterContent.scrollLeft === 0
      this.isAtEnd = filterContent.scrollLeft >= maxScrollLeft - 1
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-container {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  padding: 2px 0;

  .filter-content {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    overflow-x: auto;
    scroll-behavior: smooth;
    padding: 2px 0;
    gap: 8px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    & > button {
      flex-shrink: 0;
      color: #344264;
      font-weight: 600;
      transition: none;
      background: none;
      outline: none;
      border: none;
      border-radius: 4px;
      padding: 8px 16px;
    }

    & > button.active {
      background-color: $mep-secondary-100;
      color: $mep-primary;
      font-weight: 600;
    }
    .btn-light {
      color: $mep-color-text-400;
    }
  }
  .scroll-btn {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid #d7deea;
    border-radius: 4px;
    background: #eef2f7;
    color: $mep-color-text-300;

    .mc-fa {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      line-height: 1;
      margin: 0px;

      &::before {
        font-size: 26px;
        color: inherit;
      }
    }

    &:hover,
    &:focus,
    &:active {
      background: #e5ebf4;
      color: $mep-color-text-400;
      border-color: #d7deea;
      box-shadow: none;
    }

    &:disabled {
      opacity: 1;
      background: #eef2f7;
      color: #b7c2d6;
      border-color: #e1e6ef;
      cursor: default;
    }
  }

  &--with-navigation {
    .filter-content {
      overflow-x: hidden;
    }
  }
}
</style>
